import { CssBaseline, useMediaQuery } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ConsoleMode, buildTheme } from "../theme";

export type ColorPreference = ConsoleMode | "system";

export const THEME_STORAGE_KEY = "pft.console.theme";

const PREFERENCES: readonly ColorPreference[] = ["light", "dark", "system"];

const isPreference = (value: unknown): value is ColorPreference =>
  typeof value === "string" &&
  (PREFERENCES as readonly string[]).includes(value);

/** Absent, invalid or unreadable (private mode, blocked storage) ⇒ `"system"`. */
export const readStoredPreference = (): ColorPreference => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
};

const writeStoredPreference = (preference: ColorPreference) => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Storage blocked: the choice still applies for this page load.
  }
};

export interface ColorModeContextValue {
  /** The resolved mode in use. */
  mode: ConsoleMode;
  /** What the user chose; `"system"` follows the OS setting. */
  preference: ColorPreference;
  setPreference: (preference: ColorPreference) => void;
  /** `prefers-reduced-motion: reduce`; the theme's transition durations are 0 when true. */
  reducedMotion: boolean;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

export interface ColorModeProviderProps {
  children: ReactNode;
  /** Overrides the stored preference on first render (tests, previews). Not persisted. */
  initialPreference?: ColorPreference;
}

export const ColorModeProvider = ({
  children,
  initialPreference,
}: ColorModeProviderProps) => {
  const [preference, setPreferenceState] = useState<ColorPreference>(
    () => initialPreference ?? readStoredPreference(),
  );
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)", {
    noSsr: true,
  });
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", {
    noSsr: true,
  });
  const systemMode: ConsoleMode = prefersDark ? "dark" : "light";
  const mode: ConsoleMode = preference !== "system" ? preference : systemMode;

  const setPreference = useCallback((next: ColorPreference) => {
    const value = isPreference(next) ? next : "system";
    writeStoredPreference(value);
    setPreferenceState(value);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const theme = useMemo(
    () => buildTheme(mode, reducedMotion),
    [mode, reducedMotion],
  );
  const value = useMemo(
    () => ({ mode, preference, setPreference, reducedMotion }),
    [mode, preference, setPreference, reducedMotion],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export const useColorMode = (): ColorModeContextValue => {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error("useColorMode must be used inside <ColorModeProvider>");
  }
  return ctx;
};
